import React, { useEffect, useMemo, useState } from "react";
import { Card, Form, InputNumber, message, Select, Spin, Table, Button, Modal, Input } from "antd";
import apiClient from "../../services/apiClient";
import { useUser } from "../../context/UserContext";

const CreditRisk = () => {
  useUser();
  const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8080";
  const token = localStorage.getItem("token");

  const [sharesForm] = Form.useForm();
  const [riskFreeForm] = Form.useForm();
  const [issuerOptions, setIssuerOptions] = useState([]);

  const [sharesSubmitting, setSharesSubmitting] = useState(false);
  const [riskFreeSubmitting, setRiskFreeSubmitting] = useState(false);

  const [backfillSubmitting, setBackfillSubmitting] = useState(false);

  const [selectedIssuer, setSelectedIssuer] = useState(null);
  const [latestMerton, setLatestMerton] = useState(null);
  const [dailyRows, setDailyRows] = useState([]);
  const [sharesOutstandingHistoryRows, setSharesOutstandingHistoryRows] = useState([]);
  const [riskFreeRateHistoryRows, setRiskFreeRateHistoryRows] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [showSharesModal, setShowSharesModal] = useState(false);
  const [showRiskFreeModal, setShowRiskFreeModal] = useState(false);

  const authHeaders = useMemo(() => {
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }, [token]);

  useEffect(() => {
    const fetchIssuerOptions = async () => {
      try {
        const response = await apiClient.get(`${baseUrl}/master-issuer/issuers/basic-industry`, {
          headers: authHeaders,
        });
        if (response?.data?.statusCode === 200 && Array.isArray(response.data.data)) {
          setIssuerOptions(response.data.data);
        } else {
          setIssuerOptions([]);
        }
      } catch (e) {
        console.error(e);
        message.error("Failed to load issuer options");
      }
    };
    fetchIssuerOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshData = async (issuer) => {
    if (!issuer) return;
    setTableLoading(true);
    try {
      const enc = encodeURIComponent(issuer);

      const [latestRes, pdRes, sharesRes, rfRes] = await Promise.all([
        apiClient.get(`${baseUrl}/credit-risk/merton/${enc}/latest`, {
          headers: authHeaders,
          validateStatus: () => true,
        }),
        apiClient.get(`${baseUrl}/credit-risk/merton/${enc}/pd-history-desc`, {
          headers: authHeaders,
          validateStatus: () => true,
        }),
        apiClient.get(`${baseUrl}/credit-risk/merton-upload/${enc}/shares-outstanding/history`, {
          headers: authHeaders,
          validateStatus: () => true,
        }),
        apiClient.get(`${baseUrl}/credit-risk/merton-upload/risk-free-rate/history`, {
          headers: authHeaders,
          validateStatus: () => true,
        }),
      ]);

      const latestData = latestRes?.data?.data || null;
      setLatestMerton(latestData);

      const rows = Array.isArray(pdRes?.data?.data) ? pdRes.data.data : [];
      setDailyRows(
        rows.map((r) => ({
          key: `${r.tradeDate}-${r.pd_percent}`,
          date: r.tradeDate,
          PD_percent: r.pd_percent,
          PD: r.pd,
          risk: r.risk,
          DD: r.dd,
          D: r.d,
          E: r.e,
          r: r.r,
          T: r.T,
          sigma_e: r.sigma_e,
        }))
      );

      const sharesRows = Array.isArray(sharesRes?.data?.data) ? sharesRes.data.data : [];
      setSharesOutstandingHistoryRows(
        sharesRows.map((r) => ({
          key: r.effectiveFrom,
          effectiveFrom: r.effectiveFrom,
          sharesOutstanding: r.sharesOutstanding,
        }))
      );

      const rfRows = Array.isArray(rfRes?.data?.data) ? rfRes.data.data : [];
      setRiskFreeRateHistoryRows(
        rfRows.map((r) => ({
          key: r.effectiveFrom,
          effectiveFrom: r.effectiveFrom,
          rate: r.rate,
        }))
      );
    } catch (e) {
      console.error(e);
      message.error("Failed to refresh credit risk history");
      setDailyRows([]);
      setLatestMerton(null);
      setSharesOutstandingHistoryRows([]);
      setRiskFreeRateHistoryRows([]);
    } finally {
      setTableLoading(false);
    }
  };

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const onSubmitSharesOutstanding = async () => {
    if (!selectedIssuer) {
      message.error("Please select an issuer first");
      return;
    }
    try {
      const values = await sharesForm.validateFields();
      setSharesSubmitting(true);

      const payload = {
        effectiveFrom: values.effectiveFrom,
        sharesOutstanding: Number(values.sharesOutstanding),
      };

      await apiClient.post(
        `${baseUrl}/credit-risk/merton-upload/${encodeURIComponent(selectedIssuer)}/shares-outstanding`,
        payload,
        { headers: { ...authHeaders } }
      );

      message.success("Shares outstanding upserted successfully");
      setShowSharesModal(false);
      await refreshData(selectedIssuer);
    } catch (e) {
      console.error(e);
      message.error(
        e?.response?.data?.message || e?.message || "Failed to upsert shares outstanding"
      );
    } finally {
      setSharesSubmitting(false);
    }
  };

  const onSubmitRiskFreeRate = async () => {
    try {
      const values = await riskFreeForm.validateFields();
      setRiskFreeSubmitting(true);

      const payload = {
        effectiveFrom: values.effectiveFrom,
        rate: Number(values.rate),
      };

      await apiClient.post(`${baseUrl}/credit-risk/merton-upload/risk-free-rate`, payload, {
        headers: { ...authHeaders },
      });

      message.success("Risk-free rate upserted successfully");
      setShowRiskFreeModal(false);
      if (selectedIssuer) await refreshData(selectedIssuer);
    } catch (e) {
      console.error(e);
      message.error(
        e?.response?.data?.message || e?.message || "Failed to upsert risk-free rate"
      );
    } finally {
      setRiskFreeSubmitting(false);
    }
  };

  const onBackfillFromTradeHistory = async () => {
    try {
      setBackfillSubmitting(true);
      await apiClient.post(
        `${baseUrl}/credit-risk/merton/backfill-from-trade-history`,
        {},
        { headers: { ...authHeaders } }
      );
      message.success("Backfill started/completed from trade history (check logs for details).");
      if (selectedIssuer) await refreshData(selectedIssuer);
    } catch (e) {
      console.error(e);
      message.error(e?.response?.data?.message || e?.message || "Backfill failed");
    } finally {
      setBackfillSubmitting(false);
    }
  };

  const latestCards = [
    {
      title: "Latest PD (%)",
      value: latestMerton?.PD_percent != null ? `${Number(latestMerton.PD_percent).toFixed(2)}%` : "—",
    },
    {
      title: "Latest Distance to Default (DD)",
      value: latestMerton?.DD != null ? Number(latestMerton.DD).toFixed(2) : "—",
    },
    {
      title: "Latest Risk",
      value: latestMerton?.risk != null ? String(latestMerton.risk) : "—",
    },
    {
      title: "Latest Shares Outstanding",
      value:
        sharesOutstandingHistoryRows.length && sharesOutstandingHistoryRows[0]?.sharesOutstanding != null
          ? Number(sharesOutstandingHistoryRows[0].sharesOutstanding).toLocaleString("en-IN")
          : "—",
    },
    {
      title: "Latest Risk-free Rate (r)",
      value:
        riskFreeRateHistoryRows.length && riskFreeRateHistoryRows[0]?.rate != null
          ? Number(riskFreeRateHistoryRows[0].rate).toFixed(6)
          : "—",
    },
  ];

  const dailyColumns = [
    { title: "Date", dataIndex: "date", key: "date", width: 140 },
    { title: "PD (%)", dataIndex: "PD_percent", key: "PD_percent", width: 120 },
    { title: "PD", dataIndex: "PD", key: "PD", width: 120 },
    { title: "Risk", dataIndex: "risk", key: "risk", width: 160 },
    { title: "DD", dataIndex: "DD", key: "DD", width: 120 },
    { title: "D (debt)", dataIndex: "D", key: "D", width: 140 },
    { title: "E (equity)", dataIndex: "E", key: "E", width: 140 },
    { title: "r (risk-free)", dataIndex: "r", key: "r", width: 140 },
    { title: "sigma_e", dataIndex: "sigma_e", key: "sigma_e", width: 140 },
  ];

  const sharesHistoryColumns = [
    { title: "Effective From", dataIndex: "effectiveFrom", key: "effectiveFrom", width: 160 },
    { title: "Shares Outstanding", dataIndex: "sharesOutstanding", key: "sharesOutstanding", width: 200 },
  ];

  const riskFreeHistoryColumns = [
    { title: "Effective From", dataIndex: "effectiveFrom", key: "effectiveFrom", width: 160 },
    { title: "Rate (r)", dataIndex: "rate", key: "rate", width: 160 },
  ];

  return (
    <div className="admin-page">
      <Card className="admin-main-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Select
              style={{ minWidth: 240 }}
              showSearch
              placeholder="Select issuer"
              optionFilterProp="children"
              value={selectedIssuer || undefined}
              onChange={(value) => {
                setSelectedIssuer(value);
                refreshData(value);
              }}
              filterOption={(input, option) =>
                (option?.children || "").toLowerCase().includes(input.toLowerCase())
              }
            >
              {issuerOptions.map((it) => (
                <Select.Option key={it.issuer} value={it.issuer}>
                  {it.issuer}
                </Select.Option>
              ))}
            </Select>
          </div>
          {selectedIssuer && (
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                type="default"
                onClick={() => {
                  sharesForm.resetFields();
                  sharesForm.setFieldsValue({ effectiveFrom: todayStr });
                  setShowSharesModal(true);
                }}
              >
                Add Shares Outstanding
              </Button>
              <Button
                type="default"
                onClick={() => {
                  riskFreeForm.resetFields();
                  riskFreeForm.setFieldsValue({ effectiveFrom: todayStr });
                  setShowRiskFreeModal(true);
                }}
              >
                Add Risk-free Rate
              </Button>
            </div>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {latestCards.map((c) => (
              <Card key={c.title} size="small" style={{ minWidth: 220, flex: "1 1 220px" }} title={c.title}>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{c.value}</div>
              </Card>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginBottom: 12 }}>
          <Button type="default" onClick={onBackfillFromTradeHistory} loading={backfillSubmitting}>
            Backfill from Trade History (All Issuers)
          </Button>
          <Button type="default" onClick={() => refreshData(selectedIssuer)} disabled={!selectedIssuer}>
            Refresh Dashboard
          </Button>
        </div>

        <Spin spinning={tableLoading}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
            <Card size="small" title="Historical Merton Outputs (from PD history)" style={{ overflow: "auto" }}>
              <Table
                size="small"
                rowKey={(r) => r.key}
                columns={dailyColumns}
                dataSource={dailyRows}
                pagination={{ pageSize: 15 }}
              />
            </Card>

            <Card size="small" title="Shares Outstanding History" style={{ overflow: "auto" }}>
              <Table
                size="small"
                rowKey={(r) => r.key}
                columns={sharesHistoryColumns}
                dataSource={sharesOutstandingHistoryRows}
                pagination={{ pageSize: 10 }}
              />
            </Card>

            <Card size="small" title="Risk-free Rate History (global)" style={{ overflow: "auto" }}>
              <Table
                size="small"
                rowKey={(r) => r.key}
                columns={riskFreeHistoryColumns}
                dataSource={riskFreeRateHistoryRows}
                pagination={{ pageSize: 10 }}
              />
            </Card>
          </div>
        </Spin>

        <Modal
          title="Upsert shares outstanding history"
          open={showSharesModal}
          onCancel={() => setShowSharesModal(false)}
          footer={null}
          destroyOnClose
        >
          <Form
            form={sharesForm}
            layout="vertical"
            onFinish={onSubmitSharesOutstanding}
            initialValues={{ effectiveFrom: todayStr }}
          >
            <Form.Item
              name="effectiveFrom"
              label="effective_from (yyyy-MM-dd)"
              rules={[{ required: true, message: "effectiveFrom is required" }]}
            >
              <Input type="date" />
            </Form.Item>
            <Form.Item
              name="sharesOutstanding"
              label="Outstanding shares"
              rules={[{ required: true, message: "sharesOutstanding is required" }]}
            >
              <InputNumber style={{ width: "100%" }} min={1} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={sharesSubmitting}>
                Submit
              </Button>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Upsert risk-free rate history"
          open={showRiskFreeModal}
          onCancel={() => setShowRiskFreeModal(false)}
          footer={null}
          destroyOnClose
        >
          <Form
            form={riskFreeForm}
            layout="vertical"
            onFinish={onSubmitRiskFreeRate}
            initialValues={{ effectiveFrom: todayStr }}
          >
            <Form.Item
              name="effectiveFrom"
              label="effective_from (yyyy-MM-dd)"
              rules={[{ required: true, message: "effectiveFrom is required" }]}
            >
              <Input type="date" />
            </Form.Item>
            <Form.Item
              name="rate"
              label="Risk-free rate r"
              rules={[{ required: true, message: "rate is required" }]}
            >
              <InputNumber style={{ width: "100%" }} step={0.0001} min={0} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={riskFreeSubmitting}>
                Submit
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default CreditRisk;
